import { useCallback, useMemo, useState } from 'react';
import { dbui_v1_Notable } from '@antimatterhq/asl-api-ts-v1';
import { useSyncStateToQueryParam } from '@antimatterhq/ui/hooks/state/useSyncStateToQueryParam';
import { destr } from 'destr';
import { useCreateNotablesUpdate } from '@/hooks/dbui/useCreateNotablesUpdate';
import { useGetNotablesInfinite } from '@/hooks/dbui/useGetNotablesInfinite';

function useNotables() {
  const [includeNotOpen, setIncludeNotOpen] = useState<boolean>(
    destr<boolean>(
      new URLSearchParams(window.location.search).get('includeNotOpen'),
    ) ?? true,
  );

  const [date, setDate] = useState<Date | undefined>(
    destr<string | undefined>(
      new URLSearchParams(window.location.search).get('date'),
    )
      ? new Date(
          destr<string>(
            new URLSearchParams(window.location.search).get('date'),
          ),
        )
      : undefined,
  );

  useSyncStateToQueryParam({
    includeNotOpen,
    date,
  });

  const beforeDate = useMemo(() => date?.toISOString(), [date]);

  const {
    optimisticUpdateAssignNotables,
    optimisticUpdateStateNotables,
    optimisticUpdateNotableById,
  } = useGetNotablesInfinite({
    includeNotOpen,
    beforeDate,
  });

  const { mutateAsync: updateNotablesAsync, isPending: isUpdatingNotables } =
    useCreateNotablesUpdate();

  const updateNotableAssignee = useCallback(
    async (assignTo: string, ids: string[], onSuccess?: () => void) => {
      await updateNotablesAsync(
        {
          ids,
          assignTo,
        },
        {
          onSuccess: async () => {
            optimisticUpdateAssignNotables({
              ids,
              assignTo,
            });
            onSuccess?.();
          },
          toasts: {
            success: 'Notables updated successfully',
            error: 'Failed to update notables',
          },
        },
      );
    },
    [optimisticUpdateAssignNotables, updateNotablesAsync],
  );

  const updateNotableState = useCallback(
    async (
      newState: dbui_v1_Notable['state'],
      ids: string[],
      onSuccess?: () => void,
    ) => {
      await updateNotablesAsync(
        {
          ids,
          setState: newState,
        },
        {
          onSuccess: async () => {
            optimisticUpdateStateNotables({
              ids,
              newState,
            });
            onSuccess?.();
          },
          toasts: {
            success: 'Notables updated successfully',
            error: 'Failed to update notables',
          },
        },
      );
    },
    [updateNotablesAsync, optimisticUpdateStateNotables],
  );

  return {
    updateNotableAssignee,
    updateNotableState,
    includeNotOpen,
    date,
    setIncludeNotOpen,
    setDate,
    isUpdatingNotables,
    optimisticUpdateNotableById,
  };
}

export default useNotables;
